import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, MessageSquare } from 'lucide-react';
import EmptyState from 'components/ui/EmptyState';
import { PageHeader } from 'components/ui/page-header';
import { TablePager } from 'components/ui/pagination';
import Skeleton from 'components/ui/skeleton';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { TestFeedbackReportView, useTestFeedback } from 'modules/feedback';

/** What learners said about this test after finishing it. Each entry shows who left it. */
export const FeedbackPage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams();
  const { page, pageSize, setPage, setPageSize } = usePaginationParams();
  const { data, isLoading, isError, isFetching, refetch } = useTestFeedback(testId, page, pageSize);

  const header = <PageHeader title={t('feedback.report_title')} description={t('feedback.report_description')} />;

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy>
        {header}
        <Skeleton variant="rounded" className="h-24 w-full" />
        <Skeleton variant="rounded" className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4">
        {header}
        <EmptyState
          icon={<AlertCircle />}
          title={t('feedback.report_error_title')}
          secondaryAction={{ label: t('retry'), onClick: () => void refetch() }}
        />
      </div>
    );
  }

  if (data.summary.total === 0) {
    return (
      <div className="space-y-4">
        {header}
        <EmptyState
          icon={<MessageSquare />}
          title={t('feedback.report_empty_title')}
          description={t('feedback.report_empty_description')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {header}
      <TestFeedbackReportView
        summary={data.summary}
        items={data.items}
        isFetching={isFetching}
        footer={
          <TablePager
            page={data.meta.page}
            pageCount={data.meta.totalPages}
            pageSize={data.meta.limit}
            total={data.meta.total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            noun={t('feedback.report_noun')}
          />
        }
      />
    </div>
  );
};

export default FeedbackPage;
